import React from "react";
import { AlertCircle, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import type { ContentStage, PublishResult } from "./types";

const GOOD = new Set(["published", "active", "live", "sent", "ok", "successful", "approved", "completed", "granted"]);
const BAD = new Set(["failed", "rejected", "needs_reauth", "error", "cancelled", "canceled", "expired", "revoked"]);

const badgeClass = (status: string): string => {
  const key = status.toLowerCase();
  if (GOOD.has(key)) return "bg-emerald-50 text-emerald-700";
  if (BAD.has(key)) return "bg-red-50 text-red-700";
  return "bg-gray-100 text-gray-700";
};

export const StatusBadge: React.FC<{ status: string | null | undefined }> = ({ status }) =>
  status ? (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badgeClass(status)}`}>
      {status.replaceAll("_", " ")}
    </span>
  ) : (
    <span className="text-gray-400">—</span>
  );

const STAGES: Record<ContentStage, { label: string; className: string }> = {
  topic_only: { label: "Topic only", className: "bg-gray-100 text-gray-700" },
  creating: { label: "Creating", className: "bg-blue-50 text-blue-700" },
  ready: { label: "Ready", className: "bg-violet-50 text-violet-700" },
  scheduled: { label: "Scheduled", className: "bg-amber-50 text-amber-700" },
  published: { label: "Published", className: "bg-emerald-50 text-emerald-700" },
  failed: { label: "Failed", className: "bg-red-50 text-red-700" },
  removed: { label: "Removed", className: "bg-gray-100 text-gray-500" },
};

export const stageLabel = (stage: string): string => STAGES[stage as ContentStage]?.label ?? stage.replaceAll("_", " ");

/** The one stage everyone reads; `detail` (raw statuses) shows on hover for debugging. */
export const StageBadge: React.FC<{ stage: ContentStage; detail?: string }> = ({ stage, detail }) => {
  const meta = STAGES[stage] ?? STAGES.topic_only;
  return (
    <span
      title={detail}
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const PUBLISH_MARK: Record<string, { mark: string; className: string }> = {
  SUCCESS: { mark: "✓", className: "text-emerald-700" },
  FAILED: { mark: "✗", className: "text-red-600" },
  RUNNING: { mark: "…", className: "text-blue-700" },
  PENDING: { mark: "⏱", className: "text-amber-700" },
  ENQUEUED: { mark: "⏱", className: "text-amber-700" },
};

/** Per-platform publish chips: "Instagram ✓ · Facebook ✗" — hover a chip for its error. Cancelled platforms are hidden. */
export const PlatformChips: React.FC<{ results: PublishResult[] }> = ({ results }) => {
  const shown = results.filter((r) => r.status !== "CANCELLED");
  if (!shown.length) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
      {shown.map((r) => {
        const meta = PUBLISH_MARK[r.status] ?? { mark: "", className: "text-gray-600" };
        return (
          <span key={r.platform} title={r.error ?? r.status} className={`whitespace-nowrap capitalize ${meta.className}`}>
            {r.platform.replaceAll("_", " ")} {meta.mark}
          </span>
        );
      })}
    </span>
  );
};

export const SectionCard: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export const LoadState: React.FC<{
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}> = ({ loading, error, onRetry }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin text-violet-600" /> Loading…
      </div>
    );
  }
  if (!error) return null;
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <AlertCircle className="w-4 h-4 text-red-500" /> {error}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
      >
        Retry
      </button>
    </div>
  );
};

export interface Column<T> {
  header: string;
  render: (row: T) => React.ReactNode;
  className?: string;
}

export function DataTable<T extends { id: number | string }>({
  columns,
  rows,
  emptyText,
}: Readonly<{ columns: Column<T>[]; rows: T[]; emptyText: string }>) {
  if (rows.length === 0) {
    return <p className="py-12 text-center text-sm text-gray-400">{emptyText}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            {columns.map((c) => (
              <th key={c.header} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 last:border-0 align-top">
              {columns.map((c) => (
                <td key={c.header} className={`px-3 py-2.5 text-gray-800 ${c.className ?? ""}`}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const Pagination: React.FC<{
  page: number;
  totalPages: number;
  totalCount: number;
  onChange: (page: number) => void;
}> = ({ page, totalPages, totalCount, onChange }) => {
  if (totalPages <= 1) {
    return <p className="mt-3 text-xs text-gray-400">{totalCount} total</p>;
  }
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
      <span className="text-xs text-gray-400">
        {totalCount} total · page {page} of {totalPages}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50"
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs text-gray-500">{children}</span>
);
